import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { DashboardLayout } from "../../components/dashboard/DashboardLayout";
import { PageHeader, StatusBadge } from "../../components/dashboard/UI";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import {
  Mail, Phone, Building2, Globe, Calendar, ShieldCheck,
  Inbox as InboxIcon, CheckCircle2, RotateCcw, MessageSquareText,
} from "lucide-react";

const STATUSES = [
  { key: "all",       labelKey: "inbox.filterAll" },
  { key: "new",       labelKey: "inbox.filterNew" },
  { key: "contacted", labelKey: "inbox.filterContacted" },
  { key: "qualified", labelKey: "inbox.filterQualified" },
  { key: "closed",    labelKey: "inbox.filterClosed" },
];

const TYPE_LABEL_KEY = {
  sygnal_shield: "inbox.typeSygnal",
  atlas: "inbox.typeAtlas",
  integration: "inbox.typeIntegration",
  forensic: "inbox.typeForensic",
  general: "inbox.typeGeneral",
};

const InquiriesInbox = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const isAdmin = user && typeof user === "object" && user.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/inquiries")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, contacted: 0, qualified: 0, closed: 0 };
    items.forEach((it) => { c[it.status] = (c[it.status] || 0) + 1; });
    return c;
  }, [items]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch {
      // swallow — UI shows current state
    } finally {
      setBusyId(null);
    }
  };

  if (user === undefined || user === null) {
    return null; // wait for auth bootstrap
  }
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <PageHeader
        eyebrow={t("inbox.eyebrow")}
        title={t("inbox.title")}
        subtitle={t("inbox.subtitle")}
      />

      {/* Filters with counts */}
      <div className="mb-8 flex flex-wrap gap-2" data-testid="inbox-filters">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            data-testid={`inbox-filter-${s.key}`}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm border transition-colors flex items-center gap-2 ${
              filter === s.key
                ? "bg-[#19A7E0]/10 border-[#19A7E0]/40 text-[#19A7E0]"
                : "bg-transparent border-white/10 text-white/60 hover:border-white/30 hover:text-white"
            }`}
          >
            {t(s.labelKey)}
            <span className="bg-white/10 text-white/80 px-1.5 py-0.5 rounded-sm font-mono text-[10px]">
              {counts[s.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-4">
        {filtered.map((it) => (
          <article
            key={it.id}
            data-testid={`inbox-item-${it.id}`}
            className="card-sharp p-6"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-base font-medium tracking-tight">{it.name}</p>
                  <StatusBadge status={it.status} label={it.status.toUpperCase()} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#19A7E0]">
                    {TYPE_LABEL_KEY[it.type] ? t(TYPE_LABEL_KEY[it.type]) : it.type}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-white/75">
                  <a
                    href={`mailto:${it.email}`}
                    className="flex items-center gap-2 hover:text-[#19A7E0] transition-colors"
                    data-testid={`inbox-email-${it.id}`}
                  >
                    <Mail className="w-3.5 h-3.5 text-white/40" /> {it.email}
                  </a>
                  {it.phone && (
                    <span className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-white/40" /> {it.phone}
                    </span>
                  )}
                  {it.company && (
                    <span className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-white/40" /> {it.company}
                    </span>
                  )}
                  {it.country && (
                    <span className="flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-white/40" /> {it.country}
                    </span>
                  )}
                  <span className="flex items-center gap-2 text-white/50">
                    <Calendar className="w-3.5 h-3.5 text-white/40" />
                    {t("inbox.received")}: {new Date(it.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="mt-4 flex items-start gap-2 text-sm text-white/85 leading-relaxed">
                  <MessageSquareText className="w-3.5 h-3.5 text-[#19A7E0] mt-1 shrink-0" />
                  <p>{it.requirements}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 lg:flex-col lg:w-48 lg:shrink-0">
                {it.status !== "contacted" && it.status !== "closed" && (
                  <button
                    onClick={() => setStatus(it.id, "contacted")}
                    disabled={busyId === it.id}
                    data-testid={`inbox-mark-contacted-${it.id}`}
                    className="btn-ghost px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-2 justify-center"
                  >
                    <Mail className="w-3.5 h-3.5" /> {t("inbox.markContacted")}
                  </button>
                )}
                {it.status !== "qualified" && it.status !== "closed" && (
                  <button
                    onClick={() => setStatus(it.id, "qualified")}
                    disabled={busyId === it.id}
                    data-testid={`inbox-mark-qualified-${it.id}`}
                    className="btn-primary px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-2 justify-center"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" /> {t("inbox.markQualified")}
                  </button>
                )}
                {it.status !== "closed" && (
                  <button
                    onClick={() => setStatus(it.id, "closed")}
                    disabled={busyId === it.id}
                    data-testid={`inbox-mark-closed-${it.id}`}
                    className="btn-ghost px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-2 justify-center"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> {t("inbox.markClosed")}
                  </button>
                )}
                {it.status === "closed" && (
                  <button
                    onClick={() => setStatus(it.id, "new")}
                    disabled={busyId === it.id}
                    data-testid={`inbox-mark-new-${it.id}`}
                    className="btn-ghost px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-2 justify-center"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> {t("inbox.markNew")}
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="card-sharp p-10 text-center" data-testid="inbox-empty">
          <InboxIcon className="w-8 h-8 text-white/30 mx-auto" />
          <p className="mt-4 text-sm text-white/60">{t("inbox.noInquiries")}</p>
        </div>
      )}
    </DashboardLayout>
  );
};

export default InquiriesInbox;
